import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Mail, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di accesso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-[#FAFAFA]">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="flex items-center gap-3 mb-16 w-fit">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-serif-display text-2xl text-[#1A1A1A]">AI TricoProtocol</span>
              <span className="text-[10px] tracking-[0.25em] text-[#C5A059] uppercase mt-1">Professional Edition</span>
            </div>
          </Link>

          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-3 gold-underline inline-block">Bentornato</h1>
          <p className="text-[#737373] mb-10 mt-6">Accedi al tuo studio tricologico digitale</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all"
                  placeholder="nome@salone.it"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                <input
                  type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-12 pr-12 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none transition-all"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#737373] hover:text-[#C5A059] transition-colors rounded"
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  aria-pressed={showPassword}
                  tabIndex={0}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-[#737373] hover:text-[#C5A059] transition-colors"
                >
                  Password dimenticata?
                </Link>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full h-12 rounded-xl bg-[#1A1A1A] text-white font-medium hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>

          <p className="mt-8 text-sm text-[#737373]">
            Hai dimenticato la password?{" "}
            <Link to="/forgot-password" className="text-[#C5A059] hover:underline font-medium" data-testid="go-to-forgot">
              Recuperala
            </Link>
          </p>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative bg-gradient-to-br from-[#1A1A1A] via-[#262626] to-[#3A2F1F]">
        <div className="absolute bottom-12 left-12 right-12 animate-fade-up">
          <p className="text-xs font-medium uppercase mb-4 text-[#C5A059] opacity-80" style={{ letterSpacing: "0.08em" }}>La diagnosi tricologica reinventata</p>
          <h2 className="font-serif-display text-4xl xl:text-5xl leading-tight font-semibold text-white">
            Analisi AI in pochi secondi.<br />
            <span className="italic text-[#E5D6B8]">Protocolli su misura.</span>
          </h2>
          <p className="mt-6 text-sm text-[#CCCCCC] max-w-md">
            Lo strumento professionale per tricologi, hair stylist e saloni che vogliono offrire un servizio di eccellenza.
          </p>
        </div>
      </div>
    </div>
  );
}
